import { DIAGNOSTIC_DEFINITION_KIND, TREATMENT_DEFINITION_KIND } from "@repo/api";
import type { PatientId } from "@repo/domain";
import type { Collection, InitialQueryBuilder, UtilsRecord } from "@tanstack/db";
import { and, eq, inArray, materialize, or, toArray, useLiveQuery } from "@tanstack/react-db";
import { useCollection } from "../core/hooks";
import type {
  ActivityDefinitionRow,
  AppointmentRow,
  CarePlanRow,
  EpisodeOfCareRow,
  ObservationRow,
  PlanDefinitionRow,
  ProcedureRow,
  RequestGroupRow,
  ServiceRequestRow,
} from "../fhir/types";
import { type BookingProjectionRow, projectBookingRow } from "./order-set-booking-projection";
import { PINNED_PROTOCOL_KINDS } from "./shared/protocol-kinds";

export type OrderBookingStatus = "unscheduled" | "scheduled" | "delivered" | "notPerformed";
export type OrderSetBookingStatus = "unscheduled" | "scheduled" | "delivered";

export interface OrderSetBookingDto {
  orderSetId: string;
  patientId: string;
  /** Case whose care manager anchors booking, resolved from the plan or active case. */
  caseId?: string;
  protocolTitle: string;
  durationMinutes: number;
  isBookable: boolean;
  status: OrderSetBookingStatus;
  steps: readonly {
    stepId: string;
    orders: readonly {
      orderId: string;
      title: string;
      definition: { url: string; version: string };
      scheduling: { status: OrderBookingStatus; visitId?: string; scheduledStart?: string; isBookable: boolean };
    }[];
  }[];
  bookableOrderIds: readonly string[];
}

export interface OrderSetBookingFilters {
  ids?: readonly string[];
  patientId?: PatientId;
}

type BookingCollections = {
  carePlans: Collection<CarePlanRow, string, UtilsRecord>;
  cases: Collection<EpisodeOfCareRow, string, UtilsRecord>;
  anchors: Collection<RequestGroupRow, string, UtilsRecord>;
  orders: Collection<ServiceRequestRow, string, UtilsRecord>;
  definitions: Collection<ActivityDefinitionRow, string, UtilsRecord>;
  protocols: Collection<PlanDefinitionRow, string, UtilsRecord>;
  appointments: Collection<AppointmentRow, string, UtilsRecord>;
  procedures: Collection<ProcedureRow, string, UtilsRecord>;
  observations: Collection<ObservationRow, string, UtilsRecord>;
};

export function buildOrderSetBookingsQuery(
  q: InitialQueryBuilder,
  collections: BookingCollections,
  filters: OrderSetBookingFilters,
) {
  const { ids, patientId } = filters;
  let query = q
    .from({ orderSet: collections.anchors })
    .where(({ orderSet }) => eq(orderSet.isRedesignedOrderSet, true));
  if (ids) query = query.where(({ orderSet }) => inArray(orderSet.domainId, [...ids]));
  if (patientId) query = query.where(({ orderSet }) => eq(orderSet.patientId, patientId));
  return query.select(({ orderSet }) => ({
    anchor: { ...orderSet },
    carePlan: materialize(
      q
        .from({ carePlan: collections.carePlans })
        .where(({ carePlan }) => eq(carePlan.domainId, orderSet.carePlanId))
        .select(({ carePlan }) => ({ ...carePlan }))
        .findOne(),
    ),
    cases: toArray(
      q
        .from({ caseModel: collections.cases })
        .where(({ caseModel }) => eq(caseModel.patientJoinKey, orderSet.patientId))
        .select(({ caseModel }) => ({ ...caseModel })),
    ),
    orders: toArray(
      q
        .from({ order: collections.orders })
        .where(({ order }) => eq(order.orderSetJoinKey, orderSet.domainId))
        .select(({ order }) => ({
          ...order,
          definition: materialize(
            q
              .from({ definition: collections.definitions })
              .where(({ definition }) => eq(definition.definitionCanonical, order.definitionCanonical))
              .where(({ definition }) =>
                or(
                  eq(definition.catalogModelKind, DIAGNOSTIC_DEFINITION_KIND),
                  eq(definition.catalogModelKind, TREATMENT_DEFINITION_KIND),
                ),
              )
              .select(({ definition }) => ({ ...definition }))
              .findOne(),
          ),
          procedures: toArray(
            q
              .from({ procedure: collections.procedures })
              // Parent-scoped and sentinel-guarded, exactly as `order-set.ts`
              // joins it: a member id alone matches the identically-numbered
              // order of every other set realized from the same protocol.
              .where(({ procedure }) =>
                and(eq(procedure.orderSetJoinKey, orderSet.domainId), eq(procedure.orderJoinKey, order.orderId)),
              )
              .select(({ procedure }) => ({
                ...procedure,
                observations: toArray(
                  q
                    .from({ observation: collections.observations })
                    .where(({ observation }) => eq(observation.procedureJoinKey, procedure.domainId))
                    .select(({ observation }) => ({ ...observation })),
                ),
              })),
          ),
          observations: toArray(
            q
              .from({ observation: collections.observations })
              .where(({ observation }) =>
                and(eq(observation.orderSetJoinKey, orderSet.domainId), eq(observation.orderJoinKey, order.orderId)),
              )
              .select(({ observation }) => ({ ...observation })),
          ),
        })),
    ),
    protocolDefinition: materialize(
      q
        .from({ protocol: collections.protocols })
        .where(({ protocol }) =>
          and(
            inArray(protocol.catalogModelKind, PINNED_PROTOCOL_KINDS),
            eq(protocol.url, orderSet.protocolUrl),
            eq(protocol.version, orderSet.protocolVersion),
          ),
        )
        .select(({ protocol }) => ({ ...protocol }))
        .findOne(),
    ),
    appointments: toArray(
      q
        .from({ appointment: collections.appointments })
        .where(({ appointment }) => eq(appointment.patientId, orderSet.patientId))
        .select(({ appointment }) => ({ ...appointment })),
    ),
    procedures: toArray(
      q
        .from({ procedure: collections.procedures })
        .where(({ procedure }) => eq(procedure.patientId, orderSet.patientId))
        .select(({ procedure }) => ({ ...procedure })),
    ),
  }));
}

export interface OrderSetBookingsResult {
  bookings: OrderSetBookingDto[];
  isReady: boolean;
  isError: boolean;
}

export function useOrderSetBookings(filters: OrderSetBookingFilters = {}): OrderSetBookingDto[] {
  return useOrderSetBookingsResult(filters).bookings;
}

/** Includes hydration status so ID-based consumers can distinguish missing sets from loading. */
export function useOrderSetBookingsResult(filters: OrderSetBookingFilters = {}): OrderSetBookingsResult {
  const carePlans = useCollection("carePlan");
  const cases = useCollection("episodeOfCare");
  const anchors = useCollection("requestGroup");
  const orders = useCollection("serviceRequest");
  const definitions = useCollection("activityDefinition");
  const protocols = useCollection("planDefinition");
  const appointments = useCollection("appointment");
  const procedures = useCollection("procedure");
  const observations = useCollection("observation");
  const ids = filters.ids;
  const collections = {
    anchors,
    orders,
    definitions,
    protocols,
    appointments,
    procedures,
    observations,
    carePlans,
    cases,
  };
  const result = useLiveQuery({
    query: (q) => {
      if (ids?.length === 0) return undefined;
      return buildOrderSetBookingsQuery(q, collections, filters);
    },
  });
  // Appointment/Procedure basedOn membership is stored in reference arrays,
  // which cannot be equality-correlated; the helper resolves only that membership.
  const projected = ((result.data as BookingProjectionRow[] | undefined) ?? []).map(projectBookingRow);
  // SQL-style orderBy cannot preserve the caller's arbitrary identity-list order.
  if (ids) {
    const position = new Map(ids.map((id, index) => [id, index]));
    projected.sort((left, right) => (position.get(left.orderSetId) ?? 0) - (position.get(right.orderSetId) ?? 0));
  }
  return { bookings: projected, isReady: result.isReady, isError: result.isError };
}

export function useOrderSetBooking(id: string | undefined): OrderSetBookingDto | undefined {
  const carePlans = useCollection("carePlan");
  const cases = useCollection("episodeOfCare");
  const anchors = useCollection("requestGroup");
  const orders = useCollection("serviceRequest");
  const definitions = useCollection("activityDefinition");
  const protocols = useCollection("planDefinition");
  const appointments = useCollection("appointment");
  const procedures = useCollection("procedure");
  const observations = useCollection("observation");
  const result = useLiveQuery({
    query: (q) => {
      if (!id) return undefined;
      return buildOrderSetBookingsQuery(
        q,
        { anchors, orders, definitions, protocols, appointments, procedures, observations, carePlans, cases },
        { ids: [id] },
      ).findOne();
    },
  });
  const row = result.data as BookingProjectionRow | undefined;
  return row ? projectBookingRow(row) : undefined;
}
